import { config } from '@/config';
import { BackgroundColor, Embedded } from '@/constants/enum';
import createDOMPurify from 'isomorphic-dompurify';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnNumberedList,
  BtnUnderline,
  HtmlButton,
  Separator,
  Toolbar,
} from 'react-simple-wysiwyg';
import ColorButton from './ColorButton';
import FontFamilyButton, { FontFamily } from './FontFamilyButton';
import FontSizeButton, { FontSize, getFontSizeValue } from './FontSizeButton';
import { EditorCustomStyled } from './styled';

interface SafeHTMLProps {
  html: string;
  className?: string;
}

export const SafeHTML: React.FC<SafeHTMLProps> = ({ html, className }) => {
  const sanitizedContent = useMemo(() => {
    return createDOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'span', 'div', 'br', 'font', 'ol', 'ul', 'li', 'u'],
      ALLOWED_ATTR: ['style', 'color', 'face', 'size'],
      ADD_ATTR: ['style', 'color', 'font-family', 'font-size'],
      FORBID_TAGS: ['script', 'style', 'iframe', 'object'],
      SANITIZE_DOM: true,
      WHOLE_DOCUMENT: false,
    });
  }, [html]);

  return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
};

interface EditorCustomProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Chiều cao tối thiểu của editor theo px */
  minHeight?: string;
  /** Chiều cao tối đa của editor theo px */
  maxHeight?: string;
  /** Style tùy chỉnh cho Editor */
  styleEditor?: React.CSSProperties;
  /** Disable editor */
  disabledEditor?: boolean;
  disabledColor?: boolean;
  disabledFontSize?: boolean;
  disabledFontFamily?: boolean;
  defaultFontSize?: FontSize;
}

// Tạo một object để lưu trữ state toàn cục cho mỗi instance
const editorStates: Record<
  string,
  {
    color: string;
    fontSize: FontSize;
    lastAccessed: number; // Thêm timestamp để theo dõi
  }
> = {};

const EditorCustom: React.FC<EditorCustomProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = '150px',
  maxHeight,
  styleEditor,
  disabledEditor,
  disabledColor,
  disabledFontSize,
  disabledFontFamily,
  defaultFontSize = FontSize.Small,
}) => {
  const instanceId = useId();
  const defaultColor = BackgroundColor.White;
  const { t } = useTranslation('translation');

  // Sử dụng state local kết hợp với state toàn cục
  const [selectedColor, setSelectedColor] = useState(editorStates[instanceId]?.color || defaultColor);
  const [selectedSize, setSelectedSize] = useState(editorStates[instanceId]?.fontSize || defaultFontSize);
  const [selectedFont, setSelectedFont] = useState(FontFamily.Arial);

  const editorRef = useRef<HTMLDivElement>(null);

  const isShowButtonHtml = config.embedded === Embedded.DEV;

  const handleColorChange = useCallback(
    (color: string) => {
      setSelectedColor(color);
      editorStates[instanceId].color = color;
      editorStates[instanceId].lastAccessed = Date.now();
    },
    [instanceId],
  );

  const handleSizeChange = useCallback(
    (size: FontSize) => {
      setSelectedSize(size);
      editorStates[instanceId].fontSize = size;
      editorStates[instanceId].lastAccessed = Date.now();
    },
    [instanceId],
  );

  const handleChange = (e: any) => {
    onChange(e.target.value);
  };

  const cleanupStaleEditorStates = () => {
    const now = Date.now();
    const staleTimeout = 30 * 60 * 1000; // 30 phút

    Object.keys(editorStates).forEach((id) => {
      if (now - editorStates[id].lastAccessed > staleTimeout) {
        delete editorStates[id];
      }
    });
  };

  const handleClearFormatting = () => {
    setSelectedColor(defaultColor);
    setSelectedSize(defaultFontSize);
    setSelectedFont(FontFamily.Arial);
  };

  // Khởi tạo hoặc cập nhật thời gian truy cập
  useEffect(() => {
    if (!editorStates[instanceId]) {
      editorStates[instanceId] = {
        color: defaultColor,
        fontSize: defaultFontSize,
        lastAccessed: Date.now(),
      };
    } else {
      editorStates[instanceId].lastAccessed = Date.now();
    }

    // Chạy cleanupStaleEditorStates khi component mount
    cleanupStaleEditorStates();

    // Cleanup khi component unmount
    return () => {
      delete editorStates[instanceId];
    };
  }, [instanceId, defaultColor, defaultFontSize]);

  // Xử lý input cho từng editor
  useEffect(() => {
    const editorElement = editorRef.current;
    if (!editorElement) return;

    const handleBeforeInput = (e: InputEvent) => {
      // Chỉ xử lý nếu event xảy ra trong editor này
      if (!editorElement.contains(e.target as Node)) return;

      document.execCommand('foreColor', false, selectedColor);
      document.execCommand('fontSize', false, getFontSizeValue(selectedSize).toString());
    };

    editorElement.addEventListener('beforeinput', handleBeforeInput);
    return () => editorElement.removeEventListener('beforeinput', handleBeforeInput);
  }, [selectedColor, selectedSize, instanceId]);

  return (
    <EditorCustomStyled
      ref={editorRef}
      data-editor-id={instanceId}
      className="editor-custom-container"
      disabledEditor={disabledEditor}
    >
      <Editor
        value={value}
        disabled={disabledEditor}
        onChange={handleChange}
        className="editor-container"
        placeholder={placeholder || t('text_entry_key')}
        containerProps={{ style: { minHeight, maxHeight } }}
        style={{ lineHeight: '1', backgroundColor: '#cfcfcf', ...styleEditor }}
      >
        <Toolbar>
          <BtnBold />
          <BtnItalic />
          <BtnUnderline />
          <Separator />
          <BtnNumberedList />
          <BtnBulletList />
          {isShowButtonHtml && (
            <>
              <Separator />
              <HtmlButton />
            </>
          )}
          <Separator />
          <FontFamilyButton selectedFont={selectedFont} setSelectedFont={setSelectedFont} disabled={disabledFontFamily} />
          <ColorButton
            selectedColor={selectedColor}
            setSelectedColor={handleColorChange}
            instanceId={instanceId}
            disabled={disabledColor}
          />
          <FontSizeButton
            selectedSize={selectedSize}
            setSelectedSize={handleSizeChange}
            instanceId={instanceId}
            disabled={disabledFontSize}
          />
          <BtnClearFormatting onClick={handleClearFormatting} />
        </Toolbar>
      </Editor>
    </EditorCustomStyled>
  );
};

export default EditorCustom;
